import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useMemo } from 'react';

import Api from '../../services/api.service';

import Loading from '../../components/Loading/loading';
import { Helmet } from 'react-helmet';
import TitlePage from '../../components/title-page';
import SearchBar from '../../components/Inputs/searchbar';
import H4 from '../../components/Titles/h4';
import DescriptionSplit from '../../components/Description/description-split';
import Link from '../../components/Links/link';
import H2 from '../../components/Titles/h2';
import CardProduct from './card-product';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons';

export default function Shop() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);

    const [loadingProducts, setLoadingProducts] = useState(true);
    const [loadingCategories, setLoadingCategories] = useState(true);

    const [search, setSearch] = useState('');
    const [activeCategorySlug, setActiveCategorySlug] = useState(null);
    const [sortOrder, setSortOrder] = useState('default');

    const [errors] = useState([{}]);

    const { t, i18n } = useTranslation();

    const buildImageUrl = (src) => {
        if (!src) return '';
        if (src.startsWith('http://') || src.startsWith('https://')) return src;
        return `https://api.lartbanne.com/uploads/artbanne/${src}`;
    };

    // Chargement des produits
    useEffect(() => {
        Api.getProducts().then((response) => {
            const res = response.data;
            if (res.status) {
                const normalizedProducts = (res.data || []).map((product) => ({
                    ...product,
                    imageSrc: buildImageUrl(product.imageSrc),
                    imagesList: (product.imagesList || []).map(buildImageUrl),
                }));

                setProducts(normalizedProducts);
            } else {
                console.log('error');
            }
            setLoadingProducts(false);
        });
    }, []);

    // Chargement des catégories
    useEffect(() => {
        Api.getProductCategories().then((response) => {
            const res = response.data;
            if (res.status) {
                setCategories(res.data || []);
            } else {
                console.log('error catégories');
            }
            setLoadingCategories(false);
        });
    }, []);

    const handleSearchBarChange = (e) => {
        const { value } = e.target;
        setSearch(value);
    };

    const handleCategoryClick = (slug) => {
        // si on clique sur la catégorie déjà active -> désélection
        if (slug === activeCategorySlug) {
            setActiveCategorySlug(null);
        } else {
            setActiveCategorySlug(slug);
        }
    };

    const getProductPrice = (product) => {
        const models = Array.isArray(product.modelProduct)
            ? product.modelProduct
            : [];

        // collect all possible prices from models
        const prices = models
            .map((m) => {
                const raw =
                    m.priceTTC ??
                    m.publicPriceTTC ??
                    m.priceHT ??
                    m.publicPriceHT;
                const n = Number(raw);
                return Number.isFinite(n) ? n : null;
            })
            .filter((n) => n !== null);

        if (prices.length) {
            return Math.min(...prices);
        }

        // fallback if no model price found
        const fallback =
            product.priceTTC ??
            product.publicPriceTTC ??
            product.priceHT ??
            product.publicPriceHT;

        const n = Number(fallback);
        return Number.isFinite(n) ? n : 0;
    };

    const filteredProducts = useMemo(() => {
        const term = search.trim().toLowerCase();

        // 1. filter by search + category
        let result = (products || []).filter((p) => {
            if (activeCategorySlug && p.categorySlug !== activeCategorySlug) {
                return false;
            }

            if (!term) return true;

            const nameCurrentLang =
                (p.name && p.name[i18n.language]) ||
                (p.name && p.name.fr) ||
                '';

            const haystack = `${nameCurrentLang} ${p.slug || ''}`.toLowerCase();
            return haystack.includes(term);
        });

        // 2. sort by price (using modelProduct prices)
        if (sortOrder !== 'default') {
            result = [...result].sort((a, b) => {
                const pa = getProductPrice(a);
                const pb = getProductPrice(b);

                if (sortOrder === 'priceAsc') return pa - pb;
                if (sortOrder === 'priceDesc') return pb - pa;
                return 0;
            });
        }

        return result;
    }, [products, search, activeCategorySlug, i18n.language, sortOrder]);

    const isLoading = loadingProducts || loadingCategories;

    return (
        <div className="bg-backgroundColor">
            <Helmet>
                <title>L&apos;Artbanne - {t('shop.pageTitle')}</title>
            </Helmet>

            <div className="m-auto max-w-7xl w-5/6 pt-14">
                <TitlePage
                    title={t('shop.welcomeMessage')}
                    description={t('shop.description')}
                />

                <div className="flex flex-col-reverse md:flex-row gap-20 py-12">
                    {/* COLONNE GAUCHE : Search + catégories + contact */}
                    <div className="w-full md:w-1/4">
                        <SearchBar
                            label={t('shop.search.label')}
                            name="findAProduct"
                            value={search}
                            error={errors.searchBar}
                            onChange={handleSearchBarChange}
                        />

                        {/* Nos catégories */}
                        <div className="mt-6">
                            <H2
                                title={t('shop.categories.title')}
                                fontWeight="semibold"
                            />

                            <div className="mt-3 flex flex-wrap gap-2 flex-col">
                                {/* Catégories dynamiques */}
                                {!loadingCategories &&
                                    categories.map((cat) => {
                                        const label =
                                            (cat.name && cat.name[i18n.language]) ||
                                            (cat.name && cat.name.fr) ||
                                            cat.slug;

                                        return (
                                            <button
                                                key={cat.slug}
                                                type="button"
                                                onClick={() =>
                                                    handleCategoryClick(cat.slug)
                                                }
                                                className={"w-fit px-3 py-1 text-xs font-medium text-gray-700"}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}

                                {/* Bouton "Toutes les catégories" */}
                                <button
                                    type="button"
                                    onClick={() => setActiveCategorySlug(null)}
                                    className={"w-fit px-3 py-1 text-xs font-medium text-gray-700"}
                                >
                                    {t('shop.categories.all')}
                                </button>
                            </div>
                        </div>

                        {/* Bloc contact / aide */}
                        <div className="p-4 bg-[#638A7314] mt-6">
                            <H4
                                title={t('shop.needHelp.title')}
                                fontWeight="bold"
                            />
                            <DescriptionSplit
                                description={t('shop.needHelp.description')}
                                className="md:text-sm"
                            />

                            <H4
                                title={t('shop.needHelp.contactUs')}
                                fontWeight="bold"
                                className="mt-4"
                            />

                            <div className="flex gap-2 my-2 text-sm items-center">
                                <FontAwesomeIcon
                                    icon={faPhone}
                                    className="text-main"
                                />
                                <Link
                                    link="tel:+33387880930"
                                    text={t('shop.needHelp.phoneNumber')}
                                    disableHoverEffect={true}
                                />
                            </div>
                            <div className="flex gap-2 text-sm items-center">
                                <FontAwesomeIcon
                                    icon={faEnvelope}
                                    className="text-main"
                                />
                                <Link
                                    link="mailto:contact@lartbanne.com"
                                    text={t('shop.needHelp.email')}
                                    disableHoverEffect={true}
                                />
                            </div>
                        </div>
                    </div>

                    {/* COLONNE DROITE : Liste produits */}
                    <div className="w-full md:w-3/4">
                        <div className="flex justify-end mb-4">
                            <div className="inline-flex items-center gap-2 px-3 py-1">
                                <span className="text-xs text-gray-500">
                                    {t('shop.sort.label') || 'Filter :'}
                                </span>

                                <div className="relative">
                                    <select
                                        value={sortOrder}
                                        onChange={(e) => setSortOrder(e.target.value)}
                                        className="
                    appearance-none 
                    bg-transparent 
                    text-xs font-medium 
                    pr-6 pl-1 py-1 
                    focus:outline-none 
                    focus:ring-0 
                    cursor-pointer 
                    text-gray-700 
                    border-none
                "
                                    >
                                        <option value="default">
                                            {t('shop.sort.default') || 'Default'}
                                        </option>
                                        <option value="priceAsc">
                                            {t('shop.sort.priceAsc') || 'Price ↑'}
                                        </option>
                                        <option value="priceDesc">
                                            {t('shop.sort.priceDesc') || 'Price ↓'}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {isLoading ? (
                            <Loading />
                        ) : filteredProducts.length ? (
                            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-8 items-stretch">
                                {filteredProducts.map((product) => (
                                    <div
                                        key={product.slug || product._id}
                                        className="h-full"
                                    >
                                        <Link link={`/product/${product.slug}`} className="block h-full">
                                            <CardProduct product={product} />
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-16 text-gray-500">
                                <p className="font-semibold mb-2">
                                    {t('shop.noResultsTitle')}
                                </p>
                                <p className="text-sm">
                                    {t('shop.noResultsText')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}